// Pure rules for keeping the shared notification store in step with what
// happens to notifications — a realtime event from another screen, or the
// user's own action applied optimistically. Kept out of the store so every
// rule is unit-testable without React or the network.
// growchat-doc/manager-notification-page/spec-manager-notification-page.md
//
// One rule underlies all of them: each list always satisfies its own filter.
// Read state belongs to the whole tenant (branch), not to the viewer.

const FILTER_PREDICATES = {
  all: () => true,
  unread: (n) => !n.read,
  read: (n) => Boolean(n.read),
}

// Newest first; `id` breaks ties, matching the API's sort so a row merged in
// here lands where a refetch would put it.
export function compareNotifications(a, b) {
  if (a.created_at !== b.created_at) return a.created_at < b.created_at ? 1 : -1
  if (a.id === b.id) return 0
  return a.id < b.id ? 1 : -1
}

function mapLoadedLists(state, update) {
  let changed = false
  const lists = {}
  for (const [filter, list] of Object.entries(state.lists)) {
    const items = list.loaded ? update(list.items, FILTER_PREDICATES[filter], filter) : list.items
    if (items !== list.items) changed = true
    lists[filter] = items === list.items ? list : { ...list, items }
  }
  return changed ? lists : state.lists
}

function findKnown(state, id) {
  for (const list of Object.values(state.lists)) {
    if (!list.loaded) continue
    const found = list.items.find((n) => n.id === id)
    if (found) return found
  }
  return null
}

// `notification.new`. The event carries no unread total, so the count is
// bumped here — only once per notification, hence the already-known check.
// Ignored until the main list has loaded: that load brings both the row and
// the true count.
export function applyNew(state, notification) {
  if (!notification?.id || !state.lists.all?.loaded || findKnown(state, notification.id)) return state
  const lists = mapLoadedLists(state, (items, matches) =>
    matches(notification) ? [notification, ...items] : items
  )
  return {
    ...state,
    lists,
    unreadCount: notification.read ? state.unreadCount : state.unreadCount + 1,
  }
}

// A total carried by a realtime event (or a REST response) is the tenant's
// true count and is simply taken. Without one — the user's own action,
// applied before the server has answered — the count is estimated from the
// rows on screen and corrected when the event echoes back.
function resolveUnreadCount(state, carried, estimate) {
  if (Number.isFinite(carried)) return carried
  return Math.max(0, estimate(state.unreadCount))
}

function withChanges(state, lists, unreadCount) {
  if (lists === state.lists && unreadCount === state.unreadCount) return state
  return { ...state, lists, unreadCount }
}

// `notification.read`, or the user marking one row read.
export function applyRead(state, id, carriedUnreadCount) {
  const wasUnread = findKnown(state, id)?.read === false
  const lists = mapLoadedLists(state, (items, matches) => {
    const index = items.findIndex((n) => n.id === id)
    if (index === -1 || items[index].read) return items
    const updated = { ...items[index], read: true }
    if (!matches(updated)) return items.filter((n) => n.id !== id)
    return items.map((n) => (n.id === id ? updated : n))
  })
  const unreadCount = resolveUnreadCount(state, carriedUnreadCount, (count) => (wasUnread ? count - 1 : count))
  return withChanges(state, insertIntoReadList(state, lists, id), unreadCount)
}

// A row that just became read belongs in the Read list too — but only when
// its details are on hand (it was loaded in some list) and it falls inside
// the stretch that list has loaded. Below that stretch it would appear out
// of order; "load more" brings it in its place instead.
function insertIntoReadList(state, lists, id) {
  const readList = lists.read
  const known = findKnown(state, id)
  if (!readList?.loaded || !known || known.read || readList.items.some((n) => n.id === id)) return lists
  const row = { ...known, read: true }
  const last = readList.items[readList.items.length - 1]
  const withinLoaded = !readList.nextCursor || (last && compareNotifications(row, last) < 0)
  if (!withinLoaded) return lists
  const items = [...readList.items, row].sort(compareNotifications)
  return { ...lists, read: { ...readList, items } }
}

// `notification.read_all`, or the user marking everything read. `readBefore`
// is when the server did it, in `created_at`'s own format: a notification
// that arrived after that moment stays unread. Omitted for the user's own
// action, which covers every row on screen.
export function applyReadAll(state, readBefore, carriedUnreadCount) {
  const covered = (n) => !n.read && (!readBefore || n.created_at <= readBefore)
  const lists = mapLoadedLists(state, (items, matches) => {
    if (!items.some(covered)) return items
    return items
      .map((n) => (covered(n) ? { ...n, read: true } : n))
      .filter(matches)
  })
  const unreadCount = resolveUnreadCount(state, carriedUnreadCount, () => 0)
  // Only a server-confirmed "mark all read" (it has a `readBefore`) flags the
  // Read list: reloading it before the write has landed would bring back the
  // old rows and clear the flag.
  return withChanges(state, readBefore ? markReadListStale(lists) : lists, unreadCount)
}

// After "mark all read" the Read list gains rows this client may never have
// loaded, so it cannot be patched in place: it is flagged and reloaded the
// next time it is shown.
export function markReadListStale(lists) {
  const readList = lists.read
  if (!readList?.loaded || readList.stale) return lists
  return { ...lists, read: { ...readList, stale: true } }
}

// `notification.deleted`, or the user deleting one row.
export function applyDeleted(state, id, carriedUnreadCount) {
  const wasUnread = findKnown(state, id)?.read === false
  const lists = mapLoadedLists(state, (items) =>
    items.some((n) => n.id === id) ? items.filter((n) => n.id !== id) : items
  )
  const unreadCount = resolveUnreadCount(state, carriedUnreadCount, (count) => (wasUnread ? count - 1 : count))
  return withChanges(state, lists, unreadCount)
}

// Folds a freshly fetched first page into a list already on screen: rows it
// contains are refreshed, rows it adds are inserted in order, and rows
// beyond it are kept — so a long list the user has paged through is not cut
// back to one page just because the tab regained focus.
export function mergeFirstPage(items, firstPage) {
  const fresh = new Map(firstPage.map((n) => [n.id, n]))
  const kept = items.filter((n) => !fresh.has(n.id))
  return [...firstPage, ...kept].sort(compareNotifications)
}
